import { datadogRum } from '@datadog/browser-rum';
import { Web, Edit, NodeId, EditState, UIState, Layer, LayerKind, Resource, ResourceId } from 'web';
import { throttle } from 'lodash';
import { CompositorNodeV3 } from '@/types/nodes/compositor';
import { log } from '@/logger/logger.ts';
import { track } from '@/utils/analytics';
import { FlowStore } from './FlowStore';
import { UndoRedoEntry } from './undoRedo';
import Clock, { FrameRateMonitor } from './Clock';
import { DevPanicOverlay } from './DevPanicOverlay';
import type { LogLevel } from 'loglevel';
import type { FlowUIState } from './FlowGraph';

export type WasmErrorType = 'fatal' | 'recovered' | 'webgl-not-supported' | 'context-lost';

/**
 * Writes a layer with its content spelled out on the layer as well as referenced,
 * so a build that predates resources can still open, render and export the
 * document.
 *
 * Three shapes end up on a saved media layer: the `resource` reference this build
 * reads, the nested `content` the build before it reads, and the bare `inputId`
 * or `file` the build before that reads. Each reader takes the newest shape it
 * understands. Both older shapes are written from here, so retiring one later is
 * deleting a branch in this one function.
 *
 * Unlike the transition before it, this cannot be worked out from the layer
 * alone: the content is not on the layer any more. `resources` has to be the
 * table the same edit is writing, not the one already in the store, because an
 * entry the edit just created is not in the store yet.
 */
export function backwardsCompatibleLayer(layer: Layer, resources: Record<ResourceId, Resource>): Layer {
  const layerKind = layer.kind;
  if (layerKind.type !== 'image' && layerKind.type !== 'video' && layerKind.type !== 'audio') {
    return layer;
  }
  const content = resources[layerKind.resource]?.content;
  if (content === undefined) {
    // An edit that changed the table but arrived as `update_layer` cannot bring
    // it, so there is nothing to resolve against. Writing no legacy fields is the
    // honest outcome, and it leaves a layer only this build can read; inventing
    // them would be worse. `assertLayerResourceIsPresent` reports it.
    return layer;
  }
  return {
    ...layer,
    kind: {
      ...layerKind,
      content,
      // The two older fields are deliberately no longer part of `LayerKind`.
      // They exist only so an older build can read what this one saves.
      ...(content.type === 'input' ? { inputId: content.data } : { file: content.data }),
    } as unknown as LayerKind,
  };
}

class WasmAPILogger {
  private contextLostAt: number | undefined = undefined;
  private webglInfo: Record<string, unknown> = {};
  private frameRateMonitor = new FrameRateMonitor();

  constructor(
    private canvas: HTMLCanvasElement,
    private getLastCreationTime: () => number | undefined,
  ) {
    this.frameRateMonitor.addCallback((wideAverageFrameTime, narrowAverageFrameTime, message) => {
      track('average_framerate_drop', { wideAverageFrameTime, narrowAverageFrameTime, message });
    });
  }

  private logAverageFrameTime = throttle(
    () => {
      const averageFrameMs = this.frameRateMonitor.averageFrameTimes[1];
      if (averageFrameMs === 0) {
        return; // data not ready to report
      }
      const data = {
        averageFrameMs,
        averageFps: 1000 / averageFrameMs,
        averageWindowMs: this.frameRateMonitor.narrowWindowMs,
      };
      datadogRum.addDurationVital('wasm_avg_frame_time', {
        startTime: Date.now() - this.frameRateMonitor.narrowWindowMs,
        duration: averageFrameMs,
        context: data,
      });
      track('wasm_avg_frame_time', data);
    },
    this.frameRateMonitor.narrowWindowMs,
    { trailing: true },
  );

  log = (level: keyof LogLevel, message: string, e?: unknown, details?: Record<string, unknown>) => {
    let input: any[] = [message];
    if (e instanceof Error) {
      input = [
        message + ': ' + e.message,
        e,
        {
          name: e.name,
          errorMessage: e.message,
          stack: e.stack,
        },
      ];
    } else if (typeof e === 'string') {
      input = [message + ': ' + e];
    }
    if (details) {
      input.push(details);
    }
    const logger = log.getLogger('Wasm');
    switch (level) {
      case 'ERROR':
        logger.error(...input);
        break;
      case 'WARN':
        logger.warn(...input);
        break;
      case 'INFO':
        logger.info(...input);
        break;
      case 'DEBUG':
        logger.debug(...input);
        break;
      case 'TRACE':
        logger.trace(...input);
        break;
    }
  };

  logContext(level: keyof LogLevel, message: string, error?: unknown, details?: Record<string, unknown>) {
    const lastCreationTime = this.getLastCreationTime();
    let contextLost: boolean | null = null;
    try {
      contextLost = this.canvas.getContext('webgl2')?.isContextLost() ?? null;
    } catch {
      // Diagnostics must not interrupt context-loss recovery.
    }
    this.log(level, message, error, {
      webgl: {
        ...this.webglInfo,
        contextLost,
        canvasWidth: this.canvas.width,
        canvasHeight: this.canvas.height,
        rendererAgeMs: lastCreationTime === undefined ? undefined : performance.now() - lastCreationTime,
        recoveryElapsedMs: this.contextLostAt === undefined ? undefined : performance.now() - this.contextLostAt,
        ...details,
      },
    });
  }

  captureWebGLInfo() {
    try {
      const gl = this.canvas.getContext('webgl2');
      if (!gl || gl.isContextLost()) {
        return;
      }
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      this.webglInfo = {
        vendor: gl.getParameter(debugInfo?.UNMASKED_VENDOR_WEBGL ?? gl.VENDOR) ?? this.webglInfo.vendor,
        renderer: gl.getParameter(debugInfo?.UNMASKED_RENDERER_WEBGL ?? gl.RENDERER) ?? this.webglInfo.renderer,
        version: gl.getParameter(gl.VERSION) ?? this.webglInfo.version,
      };
    } catch {
      // Diagnostics must not interrupt renderer initialization.
    }
  }

  updateFrameRate(time: number) {
    this.frameRateMonitor.update(time);
    this.logAverageFrameTime();
  }

  resetFrameRate() {
    this.frameRateMonitor.reset();
  }

  logContextLost(e?: Event, source: 'event' | 'panic' = 'event', error?: unknown) {
    this.contextLostAt ??= performance.now();
    this.logContext(error === undefined ? 'INFO' : 'ERROR', 'Context lost', error, {
      source,
      statusMessage: (e as WebGLContextEvent | undefined)?.statusMessage,
    });
  }

  logContextRecovery(recovered: boolean) {
    if (recovered) {
      this.logContext('INFO', 'Successfully recovered from context lost');
      this.contextLostAt = undefined;
    } else {
      this.logContext('INFO', 'Failed to recover from context lost');
    }
  }

  dispose() {
    this.logAverageFrameTime.cancel();
  }
}

export class WasmAPI {
  private canvas: HTMLCanvasElement;
  private wasm: Web | undefined = undefined;
  private store: FlowStore;
  private requestRef: number;
  private editable: boolean;
  private onError: (errorType: WasmErrorType) => void;
  private updateUIState: (updater: (oldState: FlowUIState) => Partial<FlowUIState>) => void;
  private updateLegacy: (nodeId: NodeId, isSeek: boolean) => void;
  private pushUndoState: (entry: UndoRedoEntry) => void;
  private figmaThemeEnabled: boolean;
  private previewLightMode = false;
  private lastCreationTime: number | undefined = undefined;
  private clock = new Clock();
  private logger: WasmAPILogger;
  // Dev-only. A panic that re-fires immediately after recreating the instance
  // usually means the cause outlived the instance (e.g. a bad hot-reloaded
  // shader). Park instead of going fatal so the developer can fix it and retry.
  // idle → awaiting-fix (parked after panic) → retry-pending (recreate next frame)
  private developmentRecoveryStatus: 'idle' | 'awaiting-fix' | 'retry-pending' = 'idle';
  private developmentPanicOverlay = new DevPanicOverlay();

  // The canvas is frozen on its last good frame while parked, so put the beetle up
  // for exactly as long as that lasts. Going through here keeps the two in step.
  private setDevelopmentRecoveryStatus(status: typeof this.developmentRecoveryStatus) {
    this.developmentRecoveryStatus = status;
    if (status === 'awaiting-fix') {
      this.developmentPanicOverlay.show(this.requestDevelopmentRecoveryRetry);
    } else {
      this.developmentPanicOverlay.hide();
    }
  }

  // Vite calls HMR listeners synchronously, and WasmProvider registers its own
  // `shader-update` listener (which installs the new source) before any WasmAPI
  // exists. Recreating on the next frame rather than here keeps this independent of
  // that ordering: by then every listener for this event has run. The overlay click
  // uses the same path for non-shader panics.
  private requestDevelopmentRecoveryRetry = () => {
    if (this.developmentRecoveryStatus === 'awaiting-fix') {
      this.setDevelopmentRecoveryStatus('retry-pending');
    }
  };

  private animate = () => {
    if (this.developmentRecoveryStatus === 'retry-pending') {
      this.setDevelopmentRecoveryStatus('idle');
      this.createWasm();
      if (this.wasm !== undefined) {
        this.onError('recovered');
      }
    }
    this.clock.tick();
    this.logger.updateFrameRate(this.clock.currentTimeMs);

    try {
      this.call((wasm) => {
        wasm.update(Math.min(this.clock.deltaMs, 16.666 * 30));
        return [];
      });
    } catch (e) {
      this.wasm = undefined;
      this.logger.log('ERROR', 'Animation loop failure - Unknown error in Wasm', e);
      this.onError('fatal');
    }
    this.requestRef = requestAnimationFrame(this.animate);
  };

  constructor(
    canvas: HTMLCanvasElement,
    store: FlowStore,
    editable: boolean,
    {
      onError,
      updateUIState,
      pushUndoState,
      updateLegacy,
    }: {
      onError: (errorType: WasmErrorType) => void;
      updateUIState: (updater: (oldState: FlowUIState) => Partial<FlowUIState>) => void;
      pushUndoState: (entry: UndoRedoEntry) => void;
      updateLegacy: (nodeId: NodeId, isSeek: boolean) => void;
    },
    figmaThemeEnabled: boolean = false,
  ) {
    this.store = store;
    this.canvas = canvas;
    this.logger = new WasmAPILogger(canvas, () => this.lastCreationTime);
    this.editable = editable;
    this.figmaThemeEnabled = figmaThemeEnabled;
    this.onError = onError;
    this.pushUndoState = pushUndoState;
    this.updateUIState = updateUIState;
    this.updateLegacy = updateLegacy;
    this.canvas.addEventListener('webglcontextlost', this.onContextLost, false);
    this.canvas.addEventListener('webglcontextrestored', this.onContextRestored, false);
    window.addEventListener('focus', this.onFocus, false);
    window.addEventListener('blur', this.onBlur, false);
    document.addEventListener('visibilitychange', this.onVisibilityChange, false);
    // Optional calls because Vitest's SSR transform defines `import.meta.hot` as a
    // partial shim that is missing some of the HMR API.
    import.meta.hot?.on?.('shader-update', this.requestDevelopmentRecoveryRetry);

    this.requestRef = requestAnimationFrame(this.animate);

    this.createWasm();
  }

  private createWasm() {
    this.wasm = undefined;
    try {
      this.wasm = new Web(
        (nodeId: NodeId, index: number, uiState: UIState) => {
          switch (uiState.type) {
            case 'compositor': {
              this.updateUIState((s) => {
                return { compositor: { ...s.compositor, [nodeId]: uiState.data } };
              });
              break;
            }
            case 'compositor_ai': {
              this.updateUIState((s) => ({ compositorAi: { ...s.compositorAi, [nodeId]: uiState.data } }));
              break;
            }
            case 'video': {
              this.updateUIState((s) => {
                const array = [...(s.video[nodeId] ?? [])];
                array[index] = uiState.data;
                return { video: { ...s.video, [nodeId]: array } };
              });
              break;
            }
            case 'painter': {
              this.updateUIState((s) => {
                return { painter: { ...s.painter, [nodeId]: uiState.data } };
              });
              break;
            }
            case 'three_d_view': {
              this.updateUIState((s) => {
                return { threeDView: { ...s.threeDView, [nodeId]: uiState.data } };
              });
              break;
            }
            case 'cursor': {
              this.updateUIState((s) => {
                return { cursor: { ...s.cursor, [nodeId]: uiState.data } };
              });
              break;
            }
            case 'comparison': {
              this.updateUIState((s) => {
                return { comparison: { ...s.comparison, [nodeId]: uiState.data } };
              });
              break;
            }
            default: {
              const _exhaustiveCheck: never = uiState;
            }
          }
        },
        (edit: Edit) => {
          this.onEdit(edit.editState);
          this.pushUndoState(UndoRedoEntry.fromWasmEdit(edit.undoState));
        },
        this.updateLegacy,
        this.canvas,
        this.editable,
        this.figmaThemeEnabled,
      );
      this.wasm.setPreviewLightMode(this.previewLightMode);
      this.lastCreationTime = performance.now();
      this.clock.reset();
      this.logger.resetFrameRate();
      this.logger.captureWebGLInfo();
    } catch (e) {
      if (this.canvas.getContext('webgl2')?.isContextLost()) {
        this.logger.logContext('ERROR', 'Wasm creation interrupted by context loss', e, { source: 'creation' });
      } else if (typeof e === 'string' && e.includes('NotSupported')) {
        // WebGL not supported on user hardware, not possible to recover
        this.logger.log('INFO', 'Failed to create WebGL2 context', e);
        this.onError('webgl-not-supported');
      } else if (e instanceof WebAssembly.RuntimeError) {
        this.logger.log('ERROR', 'Failed to create Wasm instance - Panic in Wasm', e);
        this.onError('fatal');
      } else {
        this.logger.log('ERROR', 'Failed to create Wasm instance - Unknown error in Wasm', e);
        this.onError('fatal');
      }
    }
  }

  private tryRecover = (error: WebAssembly.RuntimeError) => {
    if (this.canvas.getContext('webgl2')?.isContextLost()) {
      this.onContextLost(undefined, 'panic', error);
      return;
    }

    // Test that we don't get an error immediately after recovering
    if (this.lastCreationTime && performance.now() - this.lastCreationTime < 2000) {
      this.wasm = undefined;
      if (import.meta.env.DEV) {
        // In dev, park instead of going fatal so the developer can fix the cause
        // and retry (e.g. a hot-reloaded shader that failed to compile).
        this.setDevelopmentRecoveryStatus('awaiting-fix');
        return;
      }
      this.logger.log('ERROR', 'Failed to recover after panic, immidiate error', error);
      this.onError('fatal');
      return;
    }

    this.createWasm();
    if (this.wasm !== undefined) {
      this.logger.log('ERROR', 'Succeeded to recover after panic', error);
      this.onError('recovered');
    } else {
      this.logger.log('ERROR', 'Failed to recover after panic', error);
    }
  };

  // Flip the WASM canvas editability in place (e.g. Version History guest mode) without
  // tearing down/recreating the graph. `this.editable` is also updated so a WebGL
  // context-loss recovery reconstructs the graph with the current editability.
  setEditable(editable: boolean) {
    this.editable = editable;
    this.call((wasm) => wasm.setEditable(editable));
  }

  /** Updates preview backgrounds and preserves the selected mode across engine recovery. */
  setPreviewLightMode(light: boolean) {
    this.previewLightMode = light;
    this.call((wasm) => wasm.setPreviewLightMode(light));
  }

  call<R>(apply: (wasm: Web) => R) {
    try {
      if (this.wasm !== undefined) {
        return apply(this.wasm);
      }
    } catch (e) {
      if (e instanceof WebAssembly.RuntimeError) {
        this.tryRecover(e);
      } else {
        throw e;
      }
    }
  }

  edit(apply: (wasm: Web) => Edit): EditState[] | undefined {
    try {
      if (this.wasm !== undefined) {
        const editState = apply(this.wasm);
        this.onEdit(editState.editState);
        return editState.undoState;
      }
    } catch (e) {
      if (e instanceof WebAssembly.RuntimeError) {
        this.tryRecover(e);
      } else {
        throw e;
      }
    }
  }

  /**
   * A layer's `kind` must arrive from the engine as a plain object. If a Rust layer kind
   * serializes as a map (for example through a flattened field), the handover turns it into
   * a JS `Map`, which saves to text as an empty `{}` and makes the document unopenable. This
   * turns that silent corruption into a loud error while developing.
   */
  private assertLayerKindIsPlain(layer: unknown) {
    if (
      import.meta.env.DEV &&
      layer !== null &&
      typeof layer === 'object' &&
      (layer as { kind?: unknown }).kind instanceof Map
    ) {
      this.logger.log(
        'ERROR',
        'Compositor layer kind crossed from WASM as a Map and will be destroyed on save; a Rust layer kind is serializing map-style.',
      );
    }
  }

  /**
   * `resources` is a Rust map, so it arrives as a JS `Map` and has to be converted
   * by hand before it reaches the store. Losing it takes every piece of content in
   * the document with it, silently, on save.
   */
  private plainResources(resources: Map<ResourceId, Resource> | undefined): Record<ResourceId, Resource> {
    const entries = Array.from(resources ?? []);
    // A single resource must not be a map either, for the same reason a layer
    // kind must not be: it would write to text as `{}` and lose that piece of
    // content on save.
    if (import.meta.env.DEV && entries.some(([, resource]) => resource instanceof Map)) {
      this.logger.log(
        'ERROR',
        'A compositor resource crossed from WASM as a Map and will be destroyed on save; a Rust resource is serializing map-style.',
      );
    }
    return Object.fromEntries(entries);
  }

  /**
   * A layer must point at content the store is about to hold. An edit that
   * changes both a layer and the resource table has to arrive as `update_node`,
   * because `update_layer` carries no table and the store would take the layer
   * and drop the resource. Nothing on the engine side is inconsistent when that
   * happens, so nothing there can catch it.
   *
   * This catches a missing resource, not a stale one: an edit that changes an
   * existing entry in place and reports only the layer still points at a valid
   * id, so the store quietly keeps the old content.
   */
  private assertLayerResourceIsPresent(layer: Layer, resources: Record<ResourceId, Resource> | undefined) {
    if (!import.meta.env.DEV) {
      return;
    }
    const { kind, fill } = layer;
    const resource =
      kind.type === 'image' || kind.type === 'video' || kind.type === 'audio'
        ? kind.resource
        : kind.type === 'shape' && fill?.type === 'image'
          ? fill.resource
          : undefined;
    if (resource !== undefined && resources?.[resource] === undefined) {
      this.logger.log(
        'ERROR',
        `Compositor layer points at resource ${resource}, which the store does not hold. An edit that changes resources must be applied through updateCompositor so it arrives as update_node.`,
      );
    }
  }

  private onEdit(editStates: EditState[]) {
    for (const editState of editStates) {
      switch (editState.type) {
        case 'update_layer': {
          const { nodeId, layerId, layer } = editState;
          this.assertLayerKindIsPlain(layer);
          this.store.updateNodeData<CompositorNodeV3>(nodeId, (prevNode) => {
            const compositorData = prevNode.data;
            // A single-layer edit carries no table, so the store's own is the
            // only one there is, and a layer pointing outside it means the edit
            // should have gone through `updateCompositor`.
            this.assertLayerResourceIsPresent(layer, compositorData.resources);
            return {
              data: {
                ...compositorData,
                layers: Object.fromEntries(
                  Object.entries(compositorData.layers).map(([id, prevLayer]) => {
                    return [
                      id,
                      id === String(layerId) ? backwardsCompatibleLayer(layer, compositorData.resources) : prevLayer,
                    ];
                  }),
                ),
              },
            };
          });
          break;
        }
        case 'update_node':
        case 'insert_node': {
          const { node, nodeId } = editState;
          if (node.kind.type === 'compv3') {
            const { kind: nodeKind, ...nodeProperties } = node;
            // Convert the table before folding the layers: each layer's legacy
            // fields are rebuilt by resolving it against this edit's table.
            const resources = this.plainResources(nodeKind.data.resources);
            this.store.updateNode(nodeId, (prevNode) => {
              const prevNodeData = prevNode.data as CompositorNodeV3;
              return {
                ...nodeProperties,
                data: {
                  ...prevNode.data,
                  data: {
                    ...prevNodeData.data,
                    ...nodeKind.data,
                    layers: Object.fromEntries(
                      Array.from(nodeKind.data.layers).map(([layerId, layer]) => {
                        this.assertLayerKindIsPlain(layer);
                        return [layerId, backwardsCompatibleLayer(layer, resources)];
                      }),
                    ),
                    resources,
                  },
                },
              };
            });
          } else {
            this.store.updateNode(nodeId, (prevNode) => {
              const { kind: nodeKind, ...nodeProperties } = node;
              return { ...nodeProperties, data: { ...prevNode.data, ...nodeKind.data } };
            });
          }
          break;
        }
        case 'remove_node': {
          // TODO
          break;
        }
      }
    }
  }

  onFocus = () => {
    cancelAnimationFrame(this.requestRef);
    this.clock.reset();
    this.logger.resetFrameRate();
    this.requestRef = requestAnimationFrame(this.animate);
  };

  // Switching tabs hides the document without blurring the window, and the browser
  // throttles rAF while hidden. Reset on return so the gap is not reported as a drop.
  onVisibilityChange = () => {
    if (document.visibilityState !== 'visible') {
      return;
    }
    this.onFocus();
  };

  onBlur = () => {
    // Keep rendering while the window is unfocused in dev so changes made in the
    // editor (e.g. hot-reloaded shaders) show up without clicking back into the app.
    if (import.meta.env.DEV) {
      return;
    }
    cancelAnimationFrame(this.requestRef);
  };

  onContextLost = (e?: Event, source: 'event' | 'panic' = 'event', error?: unknown) => {
    e?.preventDefault();
    this.logger.logContextLost(e, source, error);
    this.wasm = undefined;
    this.onError('context-lost');
  };

  onContextRestored = (_e: Event) => {
    this.logger.logContext('INFO', 'Context restored');

    this.createWasm();
    this.logger.logContextRecovery(this.wasm !== undefined);
    if (this.wasm !== undefined) {
      this.onError('recovered');
    }
  };

  dispose() {
    cancelAnimationFrame(this.requestRef);
    this.logger.dispose();
    this.canvas.removeEventListener('webglcontextlost', this.onContextLost, false);
    this.canvas.removeEventListener('webglcontextrestored', this.onContextRestored, false);
    window.removeEventListener('focus', this.onFocus, false);
    window.removeEventListener('blur', this.onBlur, false);
    document.removeEventListener('visibilitychange', this.onVisibilityChange, false);
    import.meta.hot?.off?.('shader-update', this.requestDevelopmentRecoveryRetry);
    this.developmentPanicOverlay.hide();

    this.wasm = undefined;
  }
}
